// When waivers process and when a dropped player clears, in the league's
// own time zone (LeagueSettings.identity.timeZone). Zone math goes through
// Intl so there is no dependency and no table to maintain; the same code
// runs in the Durable Object, the simulator, and the browser.

import { RECENT_ADD_HOURS, formatInZone, zoneAbbreviation } from '@fsp/domain';
import type { DropTo, WaiverRules } from './types';

type Processing = WaiverRules['processing'];

const H = 3600 * 1000;

export interface WallClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

const formatters = new Map<string, Intl.DateTimeFormat>();
function formatter(timeZone: string): Intl.DateTimeFormat {
  let f = formatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    });
    formatters.set(timeZone, f);
  }
  return f;
}

/** The wall clock in `timeZone` at instant `ms`. */
export function wallClock(ms: number, timeZone: string): WallClock {
  const parts = formatter(timeZone).formatToParts(new Date(ms));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0');
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour') % 24,
    minute: get('minute'),
  };
}

function asUtc(w: WallClock): number {
  return Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute);
}

/** Zone offset at `ms`, in ms east of UTC. */
function offsetAt(ms: number, timeZone: string): number {
  return asUtc(wallClock(ms, timeZone)) - ms;
}

/** The instant at which `timeZone` shows this wall clock; the second pass absorbs a DST edge. */
export function fromWallClock(w: WallClock, timeZone: string): number {
  const guess = asUtc(w);
  const once = guess - offsetAt(guess, timeZone);
  return guess - offsetAt(once, timeZone);
}

function parseTime(time: string): { hour: number; minute: number } {
  const [h, m] = time.split(':');
  return { hour: Number(h ?? '0'), minute: Number(m ?? '0') };
}

function weekdayOf(w: WallClock): number {
  return new Date(Date.UTC(w.year, w.month - 1, w.day)).getUTCDay();
}

function shiftDays(w: WallClock, days: number): WallClock {
  const d = new Date(Date.UTC(w.year, w.month - 1, w.day + days));
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
    day: d.getUTCDate(),
    hour: w.hour,
    minute: w.minute,
  };
}

/**
 * A week of the league's calendar as the waiver schedule reads it: when it
 * starts and when it ends. A host passes every week its calendar has, in
 * order, `off` weeks among them: a late game moves a run whether or not the
 * league plays that week.
 */
export interface ScheduleWeek {
  start: number;
  end: number;
}

/**
 * How long after a late week ends its moved run comes, at the least (B-507).
 * Half a day: an NFL week ends half a day after its last kickoff
 * (`periodsFromGames`), so this is a day after the late game, the day of claims
 * ESPN gives it.
 */
export const LATE_WEEK_LEAD_HOURS = 12;

/**
 * How much later than usual a week has to end to count as late (B-507). Half a
 * day: a game moved to another day is a day or more, and the hour the clocks
 * change is not one.
 */
export const LATE_WEEK_MIN_HOURS = 12;

const WEEK = 7 * 24 * H;

/** The first time on the schedule strictly after `from`, before any late week moves it. */
function scheduledAfter(processing: Processing, timeZone: string, from: number): number | null {
  if (processing.schedule === 'continuous') return null;
  const { hour, minute } = parseTime(processing.time);
  const base = { ...wallClock(from, timeZone), hour, minute };
  for (let days = 0; days <= 8; days++) {
    const w = shiftDays(base, days);
    if (processing.schedule === 'weekly' && weekdayOf(w) !== (processing.weekday ?? 3)) continue;
    const at = fromWallClock(w, timeZone);
    if (at > from) return at;
  }
  return null;
}

/** The last time on the schedule at or before `before`, before any late week moves it. */
function scheduledAtOrBefore(
  processing: Processing,
  timeZone: string,
  before: number,
): number | null {
  if (processing.schedule === 'continuous') return null;
  const { hour, minute } = parseTime(processing.time);
  const base = { ...wallClock(before, timeZone), hour, minute };
  for (let days = 0; days >= -8; days--) {
    const w = shiftDays(base, days);
    if (processing.schedule === 'weekly' && weekdayOf(w) !== (processing.weekday ?? 3)) continue;
    const at = fromWallClock(w, timeZone);
    if (at <= before) return at;
  }
  return null;
}

const mod = (n: number, m: number): number => ((n % m) + m) % m;

/**
 * Where in the seven-day cycle a calendar's weeks usually end: the middle one
 * of their ends, each read as a place in the cycle. Per calendar, since a pool
 * read asks the same calendar many times.
 */
const usualEnds = new WeakMap<readonly ScheduleWeek[], number>();
function usualEnd(weeks: readonly ScheduleWeek[]): number {
  let usual = usualEnds.get(weeks);
  if (usual === undefined) {
    const places = weeks.map((w) => mod(w.end, WEEK)).sort((a, b) => a - b);
    usual = places[Math.floor((places.length - 1) / 2)] ?? 0;
    usualEnds.set(weeks, usual);
  }
  return usual;
}

/** How much later than usual a week ends, within half a week either way: below zero, earlier. */
function lateBy(week: ScheduleWeek, usual: number): number {
  return mod(week.end - usual + WEEK / 2, WEEK) - WEEK / 2;
}

/**
 * When the weekly run scheduled at `scheduled` happens (B-507): then, or later
 * when it falls in the time a late week ran over.
 *
 * A LATE WEEK MOVES ITS RUN, AS ESPN AND YAHOO DO. A free agent whose game has
 * started clears at the first run after his week ends (`clearsAtAfterKickoff`).
 * A week whose games run late (a Tuesday game after Monday night, a Wednesday
 * holiday game) ends after Wednesday's run, so everyone who played in it would
 * wait for the next one and miss every game of the week after, and nobody
 * could add anyone for it. So that run moves, a day at a time at its own time
 * of day, until it comes at least `LATE_WEEK_LEAD_HOURS` after the week ends:
 * Thursday for a Tuesday game, Friday for a Wednesday one. That is ESPN's day
 * of delay for each day the week ran over, and Yahoo's Thursday. A drop's
 * claims are decided at it too, since it is the week's one run.
 *
 * WHICH RUN: one the late end passed over. A week is late when it ends at
 * least `LATE_WEEK_MIN_HOURS` later in the week than the calendar's weeks
 * usually do (`usualEnd`), and the run that moves is one that falls between
 * when it would usually have ended and when it did. A run anywhere else stays
 * where the league put it: Friday's, which a late Tuesday end does not reach;
 * one on a Monday morning or before dawn on a Tuesday, which falls inside
 * every week and would decide that week's players no later in a late one; and
 * every run around a week that ends early, as the last one does with no
 * Monday game. The last week of a calendar moves nothing, since no week after
 * it waits on its players. Only the weeks' bounds are read, so every host that
 * knows its calendar gets the same answer.
 */
export function runHappensAt(
  processing: Processing,
  timeZone: string,
  weeks: readonly ScheduleWeek[],
  scheduled: number,
): number {
  if (processing.schedule !== 'weekly' || weeks.length < 2) return scheduled;
  const i = weeks.findIndex((w) => w.start <= scheduled && scheduled < w.end);
  const week = weeks[i];
  if (week === undefined || weeks[i + 1] === undefined) return scheduled;
  const late = lateBy(week, usualEnd(weeks));
  if (late < LATE_WEEK_MIN_HOURS * H || scheduled <= week.end - late) return scheduled;
  const moved = scheduledAfter(
    { ...processing, schedule: 'daily' },
    timeZone,
    week.end + LATE_WEEK_LEAD_HOURS * H - 1,
  );
  const next = scheduledAfter(processing, timeZone, scheduled);
  return moved !== null && (next === null || moved < next) ? moved : scheduled;
}

/**
 * The first processing instant strictly after `from`. Null on a continuous
 * schedule, where every player resolves the moment its own claim period ends.
 *
 * `weeks` is the league's calendar, which moves a run a late week would
 * otherwise decide too early (`runHappensAt`). Every host that has a calendar
 * passes it; without one, the schedule as the rule reads.
 */
export function nextProcessingAt(
  processing: Processing,
  timeZone: string,
  from: number,
  weeks: readonly ScheduleWeek[] = [],
): number | null {
  if (processing.schedule !== 'weekly' || weeks.length === 0)
    return scheduledAfter(processing, timeZone, from);
  // The run scheduled at or before `from` may have moved past it. The one
  // before that cannot have: a run moves to before the next one at the latest.
  const before = scheduledAtOrBefore(processing, timeZone, from);
  if (before !== null) {
    const happens = runHappensAt(processing, timeZone, weeks, before);
    if (happens > from) return happens;
  }
  const next = scheduledAfter(processing, timeZone, from);
  return next === null ? null : runHappensAt(processing, timeZone, weeks, next);
}

/**
 * The last processing instant at or before `before`. Null on a continuous
 * schedule. `weeks` as in `nextProcessingAt`.
 */
export function lastProcessingAt(
  processing: Processing,
  timeZone: string,
  before: number,
  weeks: readonly ScheduleWeek[] = [],
): number | null {
  const last = scheduledAtOrBefore(processing, timeZone, before);
  if (last === null || processing.schedule !== 'weekly' || weeks.length === 0) return last;
  const happens = runHappensAt(processing, timeZone, weeks, last);
  if (happens <= before) return happens;
  const prior = scheduledAtOrBefore(processing, timeZone, last - 1);
  return prior === null ? null : runHappensAt(processing, timeZone, weeks, prior);
}

/**
 * When a player dropped at `droppedAt` clears waivers: the claim period
 * runs, then the next scheduled processing resolves any claims. On a
 * continuous schedule the claim period alone decides.
 */
export function clearsAtForDrop(
  rules: WaiverRules,
  timeZone: string,
  droppedAt: number,
  weeks: readonly ScheduleWeek[] = [],
): number {
  const earliest = droppedAt + rules.claimPeriodHours * H;
  if (rules.processing.schedule === 'continuous') return earliest;
  return nextProcessingAt(rules.processing, timeZone, earliest - 1, weeks) ?? earliest;
}

/**
 * Where a player dropped at `droppedAt` goes: onto waivers until
 * `clearsAtForDrop`, or straight to free agency — when the league sends no drop
 * to waivers, or when this team added the player from free agency less than
 * `RECENT_ADD_HOURS` before (PLAN-2026-09-24 §2 rule 4).
 * `addedFromFreeAgencyAt` is that add's instant, or null when the player came
 * some other way (the draft, a claim, a trade).
 *
 * The one answer for the drop itself and for every view that says where it
 * will go, in every host, so a sheet cannot promise one place and the move
 * send the player to another.
 */
export function dropDestination(
  rules: WaiverRules,
  timeZone: string,
  droppedAt: number,
  addedFromFreeAgencyAt: number | null,
  weeks: readonly ScheduleWeek[] = [],
): DropTo {
  if (!rules.droppedPlayersGoToWaivers || rules.system === 'none')
    return { kind: 'free-agency', because: 'no-waivers' };
  if (addedFromFreeAgencyAt !== null && droppedAt - addedFromFreeAgencyAt < RECENT_ADD_HOURS * H)
    return { kind: 'free-agency', because: 'recent-add' };
  return { kind: 'waivers', clearsAt: clearsAtForDrop(rules, timeZone, droppedAt, weeks) };
}

/**
 * When a free agent whose game has started comes off waivers
 * (PLAN-2026-09-24 §2 rule 6): the first run once the game-time lock lifts at
 * the end of the period, `periodEndsAt`. Until then the player is on waivers —
 * claimable, not addable — where the lock used to take the player off the
 * market altogether.
 *
 * "The next run" in the plan's words, and on a weekly schedule it is: the week
 * ends before the Wednesday run, and a week that ends after it moves the run
 * past its end (`runHappensAt`, B-507). On a daily one the next run can come
 * while the period is still being played, when the lock would refuse every
 * claim on the player as `locked`; so it is the first run after the period,
 * which is when a claim can succeed.
 *
 * Null when the lock makes no waiver player: no lock, no waivers (nothing ever
 * runs), or a continuous schedule, which has no run to wait for — there the
 * lock holds until the period ends, as before.
 *
 * The one answer for the pool, the sheets, the add's refusal and the run, in
 * every host.
 */
export function clearsAtAfterKickoff(
  rules: WaiverRules,
  timeZone: string,
  periodEndsAt: number,
  weeks: readonly ScheduleWeek[] = [],
): number | null {
  if (!rules.gameTimeLock || rules.system === 'none') return null;
  if (rules.processing.schedule === 'continuous') return null;
  return nextProcessingAt(rules.processing, timeZone, periodEndsAt - 1, weeks);
}

/**
 * Whether a free agent is on waivers at `now` because his game started, and
 * until when: `clearsAtAfterKickoff` from the kickoff, `kickoffAt`, to that
 * run, or null — before the kickoff, from the run on, and wherever
 * `clearsAtAfterKickoff` makes no waiver player. `periodEndsAt` is the end of
 * the period the game is played in.
 *
 * FROM THE KICKOFF TO THE RUN, NOT TO THE END OF THE PERIOD. The lock lifts
 * when the period ends and the run comes after it — an hour after in the
 * simulator's week (it ends at 2:00 AM Wednesday, Central, and the default run
 * is at 3:00), and longer wherever a period ends further ahead of its run. A
 * host that read "on waivers" off the lock handed the
 * player back to free agency for that hour: Add in the pool, first come first
 * served, and every claim on him decided `gone` at the run he was promised to
 * (the W3 review, 2026-09-24). So this reads the game and the period, and not
 * the lock.
 *
 * The one answer for every host: the simulator's `waiverClearsAt`, and a real
 * league's pool, which knows each player's game and its period.
 */
export function kickoffWaiverClearsAt(
  rules: WaiverRules,
  timeZone: string,
  kickoffAt: number,
  periodEndsAt: number,
  now: number,
  weeks: readonly ScheduleWeek[] = [],
): number | null {
  if (now < kickoffAt) return null;
  const clears = clearsAtAfterKickoff(rules, timeZone, periodEndsAt, weeks);
  return clears !== null && now < clears ? clears : null;
}

/** When undrafted players come off post-draft waivers. */
export function clearsAtAfterDraft(
  rules: WaiverRules,
  timeZone: string,
  draftEndedAt: number,
  weeks: readonly ScheduleWeek[] = [],
): number {
  const earliest = draftEndedAt + rules.postDraftWaiverHours * H;
  if (rules.processing.schedule === 'continuous') return earliest;
  return nextProcessingAt(rules.processing, timeZone, earliest - 1, weeks) ?? earliest;
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function clockLabel(time: string): string {
  const { hour, minute } = parseTime(time);
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12}:${String(minute).padStart(2, '0')} ${hour < 12 ? 'AM' : 'PM'}`;
}

/**
 * "Processes Wednesday at 3:00 AM. Bids are sealed until then."
 *
 * WITH `readAs`, IN THE READER'S ZONE AND NAMED (B-181): "Processes Wednesday
 * at 10:00 PM NZDT." The rule is kept in the league's zone and a run happens at
 * one instant, which falls on another clock — sometimes another day — for a
 * manager elsewhere, so the weekday and the time are read off the next run
 * itself rather than off the rule. Without it (a client with no reader, the
 * tests) the rule's own words, as before.
 */
export function processingHeadline(
  rules: WaiverRules,
  /**
   * The reader's zone and the next run. With no next run (before the draft,
   * after the season) the headline is the rule's own wording, which is in
   * the league's zone, so `leagueZone` names it, read at `at`.
   */
  readAs?: { next: number | null; zone: string; leagueZone: string; at: number },
): string {
  if (rules.system === 'none') return 'No waivers: first come, first served.';
  const sealed = rules.system === 'faab' ? ' Bids are sealed until then.' : '';
  const schedule = rules.processing.schedule;
  if (schedule === 'continuous') {
    return `Each player clears ${rules.claimPeriodHours} hours after the drop.${sealed}`;
  }
  if (readAs !== undefined && readAs.next !== null) {
    const time = formatInZone(readAs.next, readAs.zone, { hour: 'numeric', minute: '2-digit' });
    if (schedule === 'daily') return `Processes daily at ${time}.${sealed}`;
    const day = new Intl.DateTimeFormat('en-US', { weekday: 'long', timeZone: readAs.zone }).format(
      new Date(readAs.next),
    );
    // A RUN A LATE WEEK MOVED (B-507) says so: its day in the league's own
    // zone against the rule's, so a reader elsewhere is told the same.
    const later = mod(
      weekdayOf(wallClock(readAs.next, readAs.leagueZone)) - (rules.processing.weekday ?? 3),
      7,
    );
    const moved =
      later === 0
        ? ''
        : `, ${later === 1 ? 'a day' : `${later} days`} later than usual because this week’s games run late`;
    return `Processes ${day} at ${time}${moved}.${sealed}`;
  }
  const named = readAs === undefined ? '' : ` ${zoneAbbreviation(readAs.leagueZone, readAs.at)}`;
  if (schedule === 'daily')
    return `Processes daily at ${clockLabel(rules.processing.time)}${named}.${sealed}`;
  return `Processes ${WEEKDAYS[rules.processing.weekday ?? 3]} at ${clockLabel(rules.processing.time)}${named}.${sealed}`;
}
